/**
 * PAKETPOSITION MIT BESTANDTEILEN — reine Logik, kein Schreibpfad.
 *
 * Manche LV-Positionen sind Gesamtpakete, z. B. „Planungsphase" als Stueck:
 * darin stecken Vorankuendigung, SiGe-Plan, Unterlage fuer spaetere Arbeiten
 * und Handgriffe wie „Abstimmung Bauherr". Die Position darf erst abgerechnet
 * werden, wenn jeder Bestandteil erledigt oder entfallen ist. Plan und
 * Begruendungen: docs/positionsbestandteile-plan.md, Schnittstellen:
 * docs/positionsbestandteile-vertrag.md (Paket A1).
 *
 * KERNREGELN, die dieses Modul abbildet:
 *
 *  1. Zwei Ebenen, nie vermischt. „Bestandteil erledigt" ist eine interne
 *     Tatsache ohne Geld. Geld fliesst nur ueber die Position
 *     (`quantityUsed`). Nichts hier fasst eine Menge an.
 *  2. Gate. Ob die Position abrechenbar ist, sagt `bestandteileOffen` — die
 *     DATENBANK pflegt den Wert (Trigger, SQL Nr. 166), nicht der Client. Die
 *     Eintraege (`position_bestandteil_eintraege`) sind fuer Anzeige und
 *     Verlauf da; das Gate rechnet NIE aus ihnen, sonst gaebe es zwei Wahrheiten
 *     und die synchronen Geldpfade (openQuantity) saehen eine andere als der
 *     Dialog.
 *  3. Fail closed. Fehlt `bestandteileOffen` (Spalte nicht geladen, alte
 *     Sitzung, kaputter Wert), ist das Gate UNBEKANNT und die Position gilt als
 *     gesperrt — nie als „nichts offen", sonst archivierte sich ein Projekt mit
 *     unabgerechnetem Paket selbst.
 *  4. Deckel. Bei einer Paketposition ist die offene Menge nie groesser als
 *     Vertragsmenge minus abgerechnet — eine doppelte Positionsbuchung kann so
 *     nie doppelt gefordert werden.
 *  5. Automatik nur bei Eindeutigkeit. Ein Modulereignis erledigt einen
 *     Bestandteil nur, wenn im Projekt genau EIN offener passender steht
 *     (Lehre applyInspectionCounter). `passenderBestandteil` ist der Spiegel
 *     der Trigger-Regel fuer den Uebernahme-Vorschlag im Dialog.
 *  6. Rein intern. Nichts hier erzeugt Text fuer Rechnung, Leistungsnachweis,
 *     E-Rechnung oder Angebots-PDF.
 *
 * DRITTER SCHLUESSELRAUM: `BestandteilQuelle` spricht dieselben Kennungen wie
 * `projekt_unterlagen_ausgaben.unterlage` (SQL Nr. 163), NICHT die von
 * `ServiceAssignment` oder `DokumentModul`. Die einzige Abbildung steht hier.
 *
 * BLATTMODUL: importiert nur Typen und flatRateLogic (selbst ein Blatt).
 * projectLv und cloudService importieren dieses Modul — ein Import in
 * Richtung projectLv/serviceReversalLogic baute einen Zyklus. Deshalb steht
 * das Ruecknahme-Praefix 'RN-' hier als lokale Kopie (ein Test haelt es
 * zeichengleich mit serviceReversalLogic.REVERSAL_REFERENCE_PREFIX).
 * Ebenso stundenLogic.ts (Stundenkontingent): es importiert DIESES Modul, von
 * dort kommen hierher nur Typen. Deshalb wohnen `stundenAusDb` und
 * `formatStunden` hier (Eintragsleser und Verlauf brauchen sie) und werden
 * dort nur weitergereicht.
 *
 * Pure Funktionen, kein Supabase, kein React, kein Netz. Uhrzeiten nur ueber
 * Intl in Europe/Berlin; „jetzt" wird hereingereicht (heuteBerlin).
 */

import type { BestandteilQuelle, PositionsBestandteil, ServiceItem, ServiceTransaction } from '../../types';
import { isFlatRateService } from '../flatRateLogic';
// Blattmodul (nur Typen) — kein Zyklus.
import { isNepService } from '../nepPosition';
// Nur der Typ — stundenLogic importiert dieses Modul (Dateikopf).
import type { StundenProtokollEintrag } from './stundenLogic';

// ─────────────────────────────────────────────────────────────────────────────
// Quellen, Vorgaben
// ─────────────────────────────────────────────────────────────────────────────

/** Reihenfolge der Auswahl im Editor. 'hand' bewusst zuletzt. */
export const BESTANDTEIL_QUELLEN: readonly BestandteilQuelle[] = [
    'vorankuendigung',
    'sigeplan',
    'asplan',
    'unterlage',
    'baustellenordnung',
    'handbuch',
    'hand',
];

export const BESTANDTEIL_QUELLE_LABEL: Record<BestandteilQuelle, string> = {
    vorankuendigung: 'Vorankündigung',
    sigeplan: 'SiGe-Plan',
    asplan: 'A+S-Plan',
    unterlage: 'Unterlage für spätere Arbeiten',
    baustellenordnung: 'Baustellenordnung / HSE-Plan',
    handbuch: 'Koordinierungshandbuch',
    hand: 'Von Hand',
};

/** Werte von `projekt_unterlagen_ausgaben.anlass` (SQL Nr. 163). */
export type AusgabeAnlass = 'freigabe' | 'versand';

/**
 * Ab welcher Ausgabe ein Modul-Bestandteil ohne eigenes `ab` erledigt ist —
 * Betreiberentscheid E3 vom 19.10.2026: die Vorankuendigung zaehlt erst mit
 * dem VERSAND (sie ist eine Meldung an die Behoerde, eine freigegebene, aber
 * nie versandte Vorankuendigung ist nichts wert), alle anderen Unterlagen mit
 * der FREIGABE. Derselbe Vorgabewert steht im Trigger (SQL Nr. 166).
 */
export const BESTANDTEIL_AB_VORGABE: Record<Exclude<BestandteilQuelle, 'hand'>, AusgabeAnlass> = {
    vorankuendigung: 'versand',
    sigeplan: 'freigabe',
    asplan: 'freigabe',
    unterlage: 'freigabe',
    baustellenordnung: 'freigabe',
    handbuch: 'freigabe',
};

const istQuelle = (q: unknown): q is BestandteilQuelle =>
    typeof q === 'string' && (BESTANDTEIL_QUELLEN as readonly string[]).includes(q);

const istAnlass = (a: unknown): a is AusgabeAnlass => a === 'freigabe' || a === 'versand';

/** Kann ein Modul diesen Bestandteil erledigen? Unbekannte Werte: nein. */
export const istModulQuelle = (q: BestandteilQuelle): boolean => istQuelle(q) && q !== 'hand';

/**
 * Der wirksame Ausloeser eines Bestandteils: das eigene `ab`, sonst die
 * Vorgabe der Quelle. 'hand' (und jede unbekannte Quelle) → null: dort gibt es
 * keine Ausgabe, die erledigen koennte.
 */
export const effektivesAb = (b: PositionsBestandteil): AusgabeAnlass | null => {
    if (!b || !istModulQuelle(b.quelle)) return null;
    if (istAnlass(b.ab)) return b.ab;
    return BESTANDTEIL_AB_VORGABE[b.quelle as Exclude<BestandteilQuelle, 'hand'>];
};

// ─────────────────────────────────────────────────────────────────────────────
// Definition lesen
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Der Key, mit dem die App einen Bestandteil ANZEIGT und ADRESSIERT („Erledigt
 * buchen" schickt ihn an die RPC): nur Text (getrimmt) und sichere ganze
 * Zahlen (als Text — so gibt `->>` eine ganze JSON-Zahl aus). Alles andere ist
 * hier nicht lesbar und ergibt '':
 *  - Objekt/Liste: `->>` liefert den JSON-Text der Datenbank (`{"a": 1}`),
 *    `JSON.stringify` einen anders formatierten (`{"a":1}`) — der Knopf
 *    adressierte eine Kennung, die es dort nicht gibt (Codex R2a, P2).
 *  - Wahrheitswert: `->>` liefert 'true'/'false'; als Kennung sinnlos, und
 *    ueber den Editor entsteht so etwas nie.
 *  - Zahl mit Nachkommastellen oder ausserhalb des sicheren Bereichs: die
 *    Schreibweise in der Datenbank (`1.50`, `1e21` …) ist nach `JSON.parse`
 *    nicht mehr herzustellen.
 * Grenze: eine ganze Zahl, die in der Spalte als `5.0` steht, kommt hier als
 * `5` an (`JSON.parse` unterscheidet die beiden nicht) — „Erledigt buchen"
 * bekaeme dann `unbekannter_bestandteil` zurueck, nie einen falschen Eintrag.
 */
const anzeigeKey = (v: unknown): string => {
    if (typeof v === 'string') return v.trim();
    if (typeof v === 'number' && Number.isSafeInteger(v)) return String(v);
    return '';
};

/**
 * Der Key-Text, wie die Datenbank ihn ZAEHLT (`bestandteil_key`, Nr. 166:
 * `p_element ->> 'key'`, Rand weg wie `trim()`): Text getrimmt, Zahl und
 * Wahrheitswert als Text, Objekt/Liste als JSON-Text (nie leer), null/fehlend
 * ''. NUR fuer die Paketerkennung (`zaehlendeBestandteilKeys`) — nie anzeigen,
 * nie adressieren (die Formatierung weicht von der Datenbank ab).
 */
const zaehlKey = (v: unknown): string => {
    if (typeof v === 'string') return v.trim();
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    if (v !== null && typeof v === 'object') return JSON.stringify(v);
    return '';
};

/**
 * Wie viele VERSCHIEDENE Bestandteile die Datenbank in dieser Definition
 * zaehlt — jedes Objekt mit nicht leerem Key, gleich welcher Typ (Spiegel von
 * `bestandteile_offen_berechnen`, Nr. 166). 0 heisst: keine Paketposition.
 *
 * Getrennt von `normalisiereBestandteile`, weil die Liste nur LESBARE Keys
 * fuehrt: OB eine Position ein Paket ist, darf nie davon abhaengen, ob die App
 * jeden Key anzeigen kann (Codex R1/R2a). Zaehlt die Datenbank mehr, als die
 * Liste zeigt, steht der Rest als „unlesbar" da (Dialog), und das Gate bleibt
 * zu — es kommt aus `bestandteile_offen`.
 */
export const zaehlendeBestandteilKeys = (raw: unknown): number => {
    if (!Array.isArray(raw)) return 0;
    const keys = new Set<string>();
    for (const el of raw) {
        if (!el || typeof el !== 'object' || Array.isArray(el)) continue;
        const key = zaehlKey((el as Record<string, unknown>).key);
        if (key) keys.add(key);
    }
    return keys.size;
};

/**
 * Toleranter Leser fuer die jsonb-Spalte — nie Wurf, und NACHSICHTIG wie die
 * Datenbank. [] und null → null.
 *
 * Warum kein Wurf: die Spalte wird in sieben Ladewegen gelesen, und ein
 * einziger Wurf hier liesse das ganze Projekt nicht laden (Lehre Nr. 50).
 *
 * Warum nachsichtig (Codex R1, P1, 19.10.2026): OB eine Position eine
 * Paketposition ist, darf nie an dieser Anzeige-Normalisierung haengen. Die
 * Datenbank zaehlt fail closed JEDES Objekt mit nicht leerem getrimmtem `key`
 * als Bestandteil, gleich welches `label` und welche `quelle`
 * (`bestandteil_key`, Nr. 166). Verwarf der Client ein solches Element (leeres
 * Label, unbekannte Quelle), blieb von `[{"key":"planung","label":"",
 * "quelle":"hand"}]` nichts uebrig — die Position galt als gewoehnliche, das
 * Gate als „kein", und sie war abrechenbar, obwohl `bestandteile_offen = 1`
 * sie sperrte. Deshalb hier dieselbe Regel, soweit der Key LESBAR ist:
 *  - Es zaehlt jedes OBJEKT mit nicht leerem Key aus Text oder ganzer Zahl
 *    (`anzeigeKey`; Rand weg wie `trim()` — dieselben Zeichen wie
 *    `bestandteil_trim`, inkl. NBSP, Tab, BOM). Ein Key aus Objekt, Liste,
 *    Wahrheitswert oder Kommazahl ist hier nicht adressierbar und fehlt in der
 *    Liste — die Datenbank zaehlt ihn trotzdem; die PAKETERKENNUNG haengt
 *    deshalb an `zaehlendeBestandteilKeys`, nicht an dieser Liste
 *    (projectLv.bestandteilFelderAusZeile).
 *  - Leeres oder fehlendes Label → der Key steht als Label da.
 *  - Unbekannte oder fehlende Quelle → `'hand'`: der Bestandteil bleibt
 *    sichtbar und ist von Hand erledigbar; die Datenbank kennt den Key, und
 *    kein Modul erledigt ihn (dort passt eine unbekannte Quelle ebenfalls nie).
 *  - Ein ZWEITES Element mit schon gesehenem Key faellt weg (das erste
 *    gewinnt) — die Datenbank zaehlt verschiedene Keys. Doppelte Keys kann es
 *    ueber den Editor nicht geben (pruefeDefinition: key_doppelt); stehen sie
 *    trotzdem in der Spalte, wuerde ein Eintrag sonst zwei Zeilen erledigen.
 *  - Sonst verworfen wird nur, was auch die Datenbank nicht zaehlt: kein
 *    Objekt (Text, Zahl, Liste, null) oder Key leer.
 * `ab` bleibt nur bei gueltiger Modulquelle und nur mit gueltigem Wert stehen;
 * fehlt es, gilt die Vorgabe (effektivesAb). Ergebnis ist immer eine frische
 * Kopie.
 *
 * Folge fuer den Speicherweg: die gelesene Definition steht hier u. U. anders
 * da als in der Spalte (`'hand'` statt einer unbekannten Quelle). Sie wird nur
 * gesendet, wenn die Sitzung sie GEAENDERT hat (cloudService.definitionSenden
 * vergleicht genau diese Normalform) — eine bloss gelesene Definition schreibt
 * nie `'hand'` ueber den Bestand.
 */
export const normalisiereBestandteile = (raw: unknown): PositionsBestandteil[] | null => {
    if (!Array.isArray(raw)) return null;
    const out: PositionsBestandteil[] = [];
    const gesehen = new Set<string>();
    for (const el of raw) {
        if (!el || typeof el !== 'object' || Array.isArray(el)) continue;
        const o = el as Record<string, unknown>;
        const key = anzeigeKey(o.key);
        if (!key) continue;
        if (gesehen.has(key)) continue;
        gesehen.add(key);
        const label = (typeof o.label === 'string' ? o.label.trim() : '') || key;
        const quelle: BestandteilQuelle = istQuelle(o.quelle) ? o.quelle : 'hand';
        const ab = quelle !== 'hand' && istAnlass(o.ab) ? o.ab : undefined;
        out.push({ key, label, quelle, ...(ab ? { ab } : {}) });
    }
    return out.length > 0 ? out : null;
};

/**
 * Ist die Position eine Paketposition? Genau dann, wenn `bestandteile` eine
 * LISTE ist — auch eine leere.
 *
 *  - Liste mit Bestandteilen → ja.
 *  - LEERE Liste `[]` → ja: so steht eine Definition aus lauter UNLESBAREN
 *    Keys da (projectLv.bestandteilFelderAusZeile — die Datenbank zaehlt sie,
 *    die Liste kann keinen zeigen). Ein wirklich leeres oder kaputtes Array
 *    macht der Loader zu `null`, und jeder Client-Schreibweg erzeugt fuer
 *    „keine Bestandteile" `null`/`undefined` (Editor `alsBestandteilWert`,
 *    `kopiereVorlage`, `normalisiereBestandteile`) — `[]` heisst also nie
 *    „kein Paket" (Codex R3: sonst fielen bei `bestandteile_offen = 0`
 *    Paketdeckel und Tranchensperre weg). Das Gate kommt wie immer aus
 *    `bestandteileOffen`: 0 frei, > 0 gesperrt, fehlt/null unbekannt.
 *  - `null` oder fehlend → nein.
 */
export const hatBestandteile = (
    s: { bestandteile?: PositionsBestandteil[] | null } | null | undefined,
): boolean => Array.isArray(s?.bestandteile);

// ─────────────────────────────────────────────────────────────────────────────
// Gate und Deckel — die Geldseite (Vertrag mit Paket B)
// ─────────────────────────────────────────────────────────────────────────────

/**
 *  - 'kein'      — keine Paketposition, kein Gate.
 *  - 'frei'      — alle Bestandteile erledigt oder entfallen.
 *  - 'gesperrt'  — mindestens einer offen.
 *  - 'unbekannt' — Paketposition, aber der Stand fehlt. Gilt als gesperrt.
 */
export type BestandteilGate = 'kein' | 'frei' | 'gesperrt' | 'unbekannt';

/**
 * Das Gate einer Position, allein aus `bestandteileOffen` (DB-gepflegt).
 *
 * `undefined`, `null`, NaN und negative Werte sind UNBEKANNT: die Spalte hat
 * einen CHECK `>= 0`, ein negativer Wert ist also kaputt und darf nicht als
 * „frei" durchgehen. Ein Bruchwert > 0 ist gesperrt (offen ist offen).
 */
export const bestandteilGate = (s: Pick<ServiceItem, 'bestandteile' | 'bestandteileOffen'>): BestandteilGate => {
    if (!hatBestandteile(s)) return 'kein';
    const offen = s?.bestandteileOffen;
    if (typeof offen !== 'number' || !Number.isFinite(offen) || offen < 0) return 'unbekannt';
    return offen === 0 ? 'frei' : 'gesperrt';
};

/** gesperrt ODER unbekannt — die Frage jedes Geldpfads. */
export const istAbrechnungGesperrt = (s: Pick<ServiceItem, 'bestandteile' | 'bestandteileOffen'>): boolean => {
    const gate = bestandteilGate(s);
    return gate === 'gesperrt' || gate === 'unbekannt';
};

const round3 = (n: number): number => Math.round(n * 1000) / 1000;

const zahl = (v: unknown): number => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

/**
 * Kernregel 3: bei Paketpositionen hoechstens Vertragsmenge minus
 * abgerechnet; sonst Infinity (kein Deckel).
 *
 * Der Fall, fuer den es den Deckel gibt: zwei Sitzungen erledigen
 * gleichzeitig den letzten Bestandteil und buchen beide die Position
 * (`quantityUsed` 2 bei Menge 1). Ohne Deckel forderte die Rechnung 2. Nie
 * negativ — eine ueberabgerechnete Position ist ein anderer Befund, kein
 * Grund fuer eine Gutschrift aus dem Nichts. Ein unbrauchbarer Mengenwert
 * zaehlt als 0 (fail closed: lieber nichts fordern als falsch).
 */
export const paketDeckel = (
    s: Pick<ServiceItem, 'bestandteile' | 'quantityContracted' | 'quantityBilled'>,
): number => {
    if (!hatBestandteile(s)) return Infinity;
    return round3(Math.max(0, zahl(s.quantityContracted) - zahl(s.quantityBilled)));
};

// ─────────────────────────────────────────────────────────────────────────────
// Definition anlegen, kopieren, pruefen
// ─────────────────────────────────────────────────────────────────────────────

const KEY_MAX = 40;

/**
 * Stabiler Key aus dem Label (klein, ascii, '-'), eindeutig gegen
 * `vorhandene` (Suffix -2, -3 …).
 *
 * „Nie neu vergeben" (Plan, Datenmodell): Eintraege verweisen ueber den Key,
 * und ein entfernter Bestandteil kann verwaiste Eintraege hinterlassen. Ein
 * neuer Bestandteil mit demselben Key erbte sie still. Der Aufrufer reicht
 * deshalb in `vorhandene` ALLE je benutzten Keys der Position herein — die
 * aktuelle Liste, frueher entfernte (Aenderungsprotokoll) und die Keys der
 * Eintraege. Der Vergleich ist unabhaengig von Gross-/Kleinschreibung, damit
 * „Vorankuendigung" und „vorankuendigung" nie nebeneinander stehen.
 */
export const neuerBestandteilKey = (label: string, vorhandene: readonly string[]): string => {
    const basis =
        String(label ?? '')
            .toLowerCase()
            .replace(/ä/g, 'ae')
            .replace(/ö/g, 'oe')
            .replace(/ü/g, 'ue')
            .replace(/ß/g, 'ss')
            .normalize('NFKD')
            .replace(/[̀-ͯ]/g, '')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
            .slice(0, KEY_MAX)
            .replace(/-+$/g, '') || 'bestandteil';
    const belegt = new Set((vorhandene || []).map((k) => String(k ?? '').trim().toLowerCase()));
    if (!belegt.has(basis)) return basis;
    for (let n = 2; ; n++) {
        const kandidat = `${basis}-${n}`;
        if (!belegt.has(kandidat)) return kandidat;
    }
};

/**
 * Tiefe Kopie einer Vorlage (Katalog, Angebot), normalisiert; null/leer →
 * undefined. Keys bleiben — sie sind in der Vorlage schon eindeutig, und
 * dieselben Keys ueber Projekte hinweg machen Auswertungen erst moeglich.
 *
 * Kopie statt Verweis (Kernregel 8): eine spaetere Katalogaenderung darf
 * laufende Projekte nie aendern. `undefined` statt `null` fuer „nichts", damit
 * der Speicherweg den Schluessel gar nicht erst mitsendet.
 */
export const kopiereVorlage = (
    vorlage: PositionsBestandteil[] | null | undefined,
): PositionsBestandteil[] | undefined => normalisiereBestandteile(vorlage) ?? undefined;

export type DefinitionsFehler =
    | 'nep_position'
    | 'menge_nicht_eins'
    | 'pauschalposition'
    | 'pauschal_gedeckt'
    | 'key_doppelt'
    | 'label_leer'
    | 'ab_bei_hand'
    | 'quelle_unbekannt';

/**
 * Prueft eine NEUE Definition fuer eine Position. Leere Liste ist immer ok
 * (= Bestandteile entfernen).
 *
 * Positionsregeln zuerst, weil sie fuer die ganze Liste gelten:
 *  - Keine Eventualposition (NEP, `is_optional`): sie ist nicht beauftragt,
 *    ein Paket darauf wuerde mit dem letzten Bestandteil automatisch gebucht
 *    — eine Leistung, die niemand bestellt hat (Pruefbefund 19.10.2026).
 *  - E5: nur Positionen mit beauftragter Menge 1. Bei Menge 3 waere „alle
 *    Bestandteile erledigt" nicht zu deuten (einmal? dreimal?).
 *  - Nicht auf einer Monatspauschale (`flatRate` oder Zuordnung 'flatrate')
 *    und nicht auf einer von ihr GEDECKTEN Position (`flatCoveredBy`): beide
 *    Mechanismen frieren den Geldstand ein, und zwei Regeln, die dieselbe Zahl
 *    stilllegen, laufen frueher oder spaeter auseinander.
 * Dann je Element in Listenreihenfolge; der erste Fehler gewinnt.
 *
 * Ein leerer KEY meldet sich als 'label_leer' (eigener Text): der Editor
 * erzeugt Keys immer selbst (neuerBestandteilKey), ein leerer Key ist ein
 * kaputtes Element, das neu angelegt werden muss — wie eines ohne Label.
 */
export const pruefeDefinition = (
    service: ServiceItem,
    neu: PositionsBestandteil[],
): { ok: true } | { ok: false; fehler: DefinitionsFehler; text: string } => {
    const liste = Array.isArray(neu) ? neu : [];
    if (liste.length === 0) return { ok: true };

    if (isNepService(service)) {
        return {
            ok: false,
            fehler: 'nep_position',
            text: 'Eine Eventualposition (NEP) kann kein Paket sein.',
        };
    }
    const menge = zahl(service?.quantityContracted);
    if (Math.abs(menge - 1) > 0.0005) {
        return {
            ok: false,
            fehler: 'menge_nicht_eins',
            text: `Bestandteile gibt es nur bei Positionen mit beauftragter Menge 1 (diese Position: ${round3(menge)} ${service?.unit || ''}).`
                .replace(' )', ')'),
        };
    }
    if (service?.assignment === 'flatrate' || isFlatRateService(service)) {
        return {
            ok: false,
            fehler: 'pauschalposition',
            text: 'Eine Monatspauschale kann keine Bestandteile haben.',
        };
    }
    if (service?.flatCoveredBy) {
        return {
            ok: false,
            fehler: 'pauschal_gedeckt',
            text: 'Diese Position ist durch eine Monatspauschale gedeckt. Bestandteile und Pauschaldeckung schließen sich aus.',
        };
    }

    const keys = new Set<string>();
    for (const b of liste) {
        const label = typeof b?.label === 'string' ? b.label.trim() : '';
        const key = typeof b?.key === 'string' ? b.key.trim() : '';
        const name = label || key || 'ohne Bezeichnung';
        if (!istQuelle(b?.quelle)) {
            return {
                ok: false,
                fehler: 'quelle_unbekannt',
                text: `Unbekannte Quelle „${String(b?.quelle ?? '')}“ bei „${name}“.`,
            };
        }
        if (!label) {
            return { ok: false, fehler: 'label_leer', text: 'Jeder Bestandteil braucht eine Bezeichnung.' };
        }
        if (!key) {
            return {
                ok: false,
                fehler: 'label_leer',
                text: `„${label}“ hat keine Kennung. Bitte den Bestandteil entfernen und neu anlegen.`,
            };
        }
        if (b.quelle === 'hand' && b.ab !== undefined && b.ab !== null) {
            return {
                ok: false,
                fehler: 'ab_bei_hand',
                text: `„${label}“ wird von Hand erledigt und hat deshalb keinen Auslöser (Freigabe/Versand).`,
            };
        }
        if (keys.has(key)) {
            return { ok: false, fehler: 'key_doppelt', text: `Die Kennung „${key}“ steht zweimal in der Liste.` };
        }
        keys.add(key);
    }
    return { ok: true };
};

/**
 * Aenderung gegenueber der alten Definition: Keys, die wegfallen, neu sind
 * oder sich inhaltlich geaendert haben (Label, Quelle, wirksamer Ausloeser).
 *
 * Verglichen wird der WIRKSAME Ausloeser (effektivesAb), nicht das rohe Feld:
 * „Vorankuendigung ohne ab" und „Vorankuendigung, ab versand" erledigen sich
 * identisch, ein „geaendert" dort waere eine Warnung ohne Gehalt. Die
 * Reihenfolge folgt der Liste (entfernt: alte Liste, sonst neue).
 */
export const definitionsDiff = (
    alt: PositionsBestandteil[] | null | undefined,
    neu: PositionsBestandteil[] | null | undefined,
): { entfernt: string[]; neu: string[]; geaendert: string[] } => {
    const a = normalisiereBestandteile(alt) ?? [];
    const n = normalisiereBestandteile(neu) ?? [];
    const altNachKey = new Map(a.map((b) => [b.key, b] as const));
    const neuKeys = new Set(n.map((b) => b.key));
    const geaendert: string[] = [];
    const neuListe: string[] = [];
    for (const b of n) {
        const vorher = altNachKey.get(b.key);
        if (!vorher) {
            neuListe.push(b.key);
        } else if (vorher.label !== b.label || vorher.quelle !== b.quelle || effektivesAb(vorher) !== effektivesAb(b)) {
            geaendert.push(b.key);
        }
    }
    return {
        entfernt: a.filter((b) => !neuKeys.has(b.key)).map((b) => b.key),
        neu: neuListe,
        geaendert,
    };
};

// ─────────────────────────────────────────────────────────────────────────────
// Stunden: Zahlen lesen und anzeigen (Stundenkontingent, Nr. 171/172)
// ─────────────────────────────────────────────────────────────────────────────
//
// Die Regeln rund um Kontingent und Ampel stehen in stundenLogic.ts. Hier nur
// das, was auch der Eintragsleser und der Verlauf brauchen (Dateikopf).

/**
 * DB-numeric (string | number | null) → number | null; nie NaN.
 *
 * PostgREST liefert `numeric` je nach Weg als Zahl oder als Text ('62.50').
 * Leer, kein Zahlwert, ±Infinity, Wahrheitswert, Objekt → null. Der
 * Wertebereich wird hier NICHT geprueft — das tun die Aufrufer je nach
 * Bedeutung (Kontingent > 0, Stunden ≥ 0).
 */
export const stundenAusDb = (raw: unknown): number | null => {
    let n: number;
    if (typeof raw === 'number') n = raw;
    else if (typeof raw === 'string' && raw.trim()) n = Number(raw.trim());
    else return null;
    if (!Number.isFinite(n)) return null;
    return n === 0 ? 0 : n; // -0 → 0
};

/**
 * Auf zwei Nachkommastellen, kaufmaennisch — ueber die Dezimalschreibweise
 * statt `Math.round(n * 100) / 100`, das 1.005 zu 1 macht (1.005 * 100 ist
 * binaer 100.4999…). Nie -0.
 */
const rundeStunden = (n: number): number => {
    const r = Number(`${Math.round(Number(`${n}e2`))}e-2`);
    const wert = Number.isFinite(r) ? r : Math.round(n * 100) / 100;
    return wert === 0 ? 0 : wert;
};

// Intl-Formatierer sind teuer im Aufbau — einmal je Modul, erst bei Bedarf.
let stundenFormat: Intl.NumberFormat | null = null;
let stundenFormatGruppiert: Intl.NumberFormat | null = null;

/**
 * Die Zahl allein, de-DE: 62.5 → '62,5', 100 → '100', 0.25 → '0,25',
 * 12345.5 → '12.345,5'. Hoechstens zwei Nachkommastellen, ohne Nullen am Ende.
 * Tausenderpunkt erst ab 10.000 (Kontingente bis 99.999 h) — „9999 h" liest
 * sich in einer Stundenliste besser als „9.999 h". Kein Zahlwert → '0'.
 */
export const formatStundenZahl = (h: number): string => {
    const n = Number(h);
    if (!Number.isFinite(n)) return '0';
    const wert = rundeStunden(n);
    const fmt = Math.abs(wert) >= 10000
        ? (stundenFormatGruppiert ??= new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2, useGrouping: true }))
        : (stundenFormat ??= new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2, useGrouping: false }));
    return fmt.format(wert);
};

/** 62.5 → '62,5 h', 100 → '100 h', 0.25 → '0,25 h' (de-DE, ohne Tausenderpunkt bis 9999, mit darüber). */
export const formatStunden = (h: number): string => `${formatStundenZahl(h)} h`;

// ─────────────────────────────────────────────────────────────────────────────
// Eintraege und Aenderungen (Zeilen der beiden Tabellen, SQL Nr. 165)
// ─────────────────────────────────────────────────────────────────────────────

/** Zeile aus `position_bestandteil_eintraege`, camelCase. Zeitpunkt und Person setzt der Server. */
export interface BestandteilEintrag {
    id: string;
    projectId: string;
    serviceId: string;
    bestandteilKey: string;
    art: 'erledigt' | 'entfaellt';
    /** YYYY-MM-DD — der Tag der Leistung, frei waehlbar, nie nach dem Erfassungstag. */
    leistungstag: string;
    durch: 'modul' | 'hand';
    ausgabeId: string | null;
    notiz: string | null;
    /** Erfassungszeitpunkt (Server-now()), nicht aenderbar. */
    erfasstAm: string;
    erfasstVon: string | null;
    erfasstVonName: string | null;
    zurueckgenommenAm: string | null;
    zurueckgenommenVon: string | null;
    zurueckgenommenVonName: string | null;
    zurueckgenommenGrund: string | null;
    /**
     * Geleistete Stunden (Spalte `stunden`, Nr. 171) — null: nicht erfasst
     * (alter Tab, Modul-Erledigung vor dem Pflichtfenster, „entfaellt" ohne
     * Angabe) oder die Spalte gibt es in dieser Datenbank noch nicht.
     * `eintragAusZeile` setzt das Feld immer. Optional nur, damit bestehende
     * Literale (Testfixturen anderer Pakete) ohne das Feld gueltig bleiben.
     */
    stunden?: number | null;
}

/** Zeile aus `position_bestandteil_aenderungen` — vorher/nachher der Definition. */
export interface BestandteilAenderung {
    id: string;
    projectId: string;
    serviceId: string;
    vorher: PositionsBestandteil[] | null;
    nachher: PositionsBestandteil[] | null;
    geaendertAm: string;
    geaendertVon: string | null;
    geaendertVonName: string | null;
}

/** Nicht-leerer Text oder null. Zahlen (z. B. eine integer-id) werden zu Text. */
const text = (v: unknown): string | null => {
    if (typeof v === 'string') {
        const t = v.trim();
        return t ? t : null;
    }
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
    return null;
};

/** 'YYYY-MM-DD' aus einem date- oder timestamp-Wert; sonst null. */
const kalendertag = (v: unknown): string | null => {
    const t = text(v);
    const m = t ? /^(\d{4}-\d{2}-\d{2})/.exec(t) : null;
    return m ? m[1] : null;
};

/**
 * Zeile aus der DB (snake_case) → BestandteilEintrag; null bei unbrauchbarer
 * Zeile (Pflichtfeld fehlt, unbekannte `art`/`durch`). Nie Wurf — eine kaputte
 * Zeile darf den Dialog nicht leeren, sie faellt nur heraus.
 *
 * `stunden` ist kein Pflichtfeld: fehlt die Spalte (Datenbank ohne Nr. 171,
 * Rueckfall im Dienst) oder ist der Wert unlesbar oder negativ, steht null da.
 */
export const eintragAusZeile = (row: Record<string, unknown>): BestandteilEintrag | null => {
    if (!row || typeof row !== 'object') return null;
    const id = text(row.id);
    const projectId = text(row.project_id);
    const serviceId = text(row.service_id);
    const bestandteilKey = text(row.bestandteil_key);
    const art = row.art;
    const durch = row.durch;
    const leistungstag = kalendertag(row.leistungstag);
    const erfasstAm = text(row.erfasst_am);
    if (!id || !projectId || !serviceId || !bestandteilKey || !leistungstag || !erfasstAm) return null;
    if (art !== 'erledigt' && art !== 'entfaellt') return null;
    if (durch !== 'modul' && durch !== 'hand') return null;
    const stunden = stundenAusDb(row.stunden);
    return {
        id,
        projectId,
        serviceId,
        bestandteilKey,
        art,
        leistungstag,
        durch,
        ausgabeId: text(row.ausgabe_id),
        notiz: text(row.notiz),
        erfasstAm,
        erfasstVon: text(row.erfasst_von),
        erfasstVonName: text(row.erfasst_von_name),
        zurueckgenommenAm: text(row.zurueckgenommen_am),
        zurueckgenommenVon: text(row.zurueckgenommen_von),
        zurueckgenommenVonName: text(row.zurueckgenommen_von_name),
        zurueckgenommenGrund: text(row.zurueckgenommen_grund),
        // Der CHECK (Nr. 171) verbietet negative Werte; steht trotzdem einer
        // da, gilt er als „nicht erfasst" statt die Summe zu verkleinern.
        stunden: stunden !== null && stunden >= 0 ? stunden : null,
    };
};

/** Zeile aus der DB (snake_case) → BestandteilAenderung; null bei unbrauchbarer Zeile. */
export const aenderungAusZeile = (row: Record<string, unknown>): BestandteilAenderung | null => {
    if (!row || typeof row !== 'object') return null;
    const id = text(row.id);
    const projectId = text(row.project_id);
    const serviceId = text(row.service_id);
    const geaendertAm = text(row.geaendert_am);
    if (!id || !projectId || !serviceId || !geaendertAm) return null;
    return {
        id,
        projectId,
        serviceId,
        vorher: normalisiereBestandteile(row.vorher),
        nachher: normalisiereBestandteile(row.nachher),
        geaendertAm,
        geaendertVon: text(row.geaendert_von),
        geaendertVonName: text(row.geaendert_von_name),
    };
};

// ─────────────────────────────────────────────────────────────────────────────
// Stand einer Position (Anzeige — das Gate kommt aus bestandteileOffen)
// ─────────────────────────────────────────────────────────────────────────────

export interface BestandteilZeile {
    bestandteil: PositionsBestandteil;
    status: 'offen' | 'erledigt' | 'entfaellt';
    eintrag: BestandteilEintrag | null;
}

export interface BestandteilStand {
    zeilen: BestandteilZeile[];
    gesamt: number;
    erledigt: number;
    entfallen: number;
    offen: number;
    /** Alle erledigt oder entfallen — und es GIBT Bestandteile. */
    vollstaendig: boolean;
}

/**
 * Zeitwert eines Zeitstempels fuer Vergleiche; NaN wird zu -Infinity (ganz
 * alt). Postgres liefert je nach Weg '2026-10-16T12:32:00+00:00' oder
 * '2026-10-16 12:32:00.123456+00' — das zweite versteht nicht jede
 * JS-Engine, deshalb wird es vorher in ISO-Form gebracht. Reine
 * Stringvergleiche taugen ueber gemischte Formate nicht.
 */
const zeitWert = (iso: string | null | undefined): number => {
    let s = String(iso ?? '').trim();
    if (!s) return -Infinity;
    if (/^\d{4}-\d{2}-\d{2} \d/.test(s)) s = s.replace(' ', 'T');
    if (/T.*[+-]\d{2}$/.test(s)) s = `${s}:00`;
    const ms = Date.parse(s);
    return Number.isFinite(ms) ? ms : -Infinity;
};

/** Aufsteigender Vergleich zweier Zeitwerte — ohne NaN aus `-Infinity - -Infinity`. */
const zeitVergleich = (a: number, b: number): number => (a === b ? 0 : a < b ? -1 : 1);

/**
 * Der Stand einer Position aus ihren Eintraegen.
 *
 * Nur AKTIVE Eintraege (`zurueckgenommenAm` null) DIESER Position zaehlen.
 * Keys ausserhalb der Definition (verwaiste Eintraege eines entfernten
 * Bestandteils) werden ignoriert, nie umgedeutet. Stehen je Key zwei aktive
 * Eintraege (der Teil-Unique der DB verhindert das — Altbestand, Handeingriff),
 * gilt der juengste nach `erfasstAm`.
 *
 * Nur fuer die Anzeige. Ob die Position abrechenbar ist, entscheidet
 * bestandteilGate ueber den DB-Wert — die beiden koennen fuer einen Moment
 * auseinanderliegen (frisch erledigt, Position noch nicht neu geladen).
 */
export const bestandteilStand = (
    service: Pick<ServiceItem, 'id' | 'bestandteile'>,
    eintraege: readonly BestandteilEintrag[],
): BestandteilStand => {
    const definition = normalisiereBestandteile(service?.bestandteile) ?? [];
    const serviceId = String(service?.id ?? '');
    const keys = new Set(definition.map((b) => b.key));
    const aktiv = new Map<string, BestandteilEintrag>();
    for (const e of eintraege || []) {
        if (!e || String(e.serviceId) !== serviceId || e.zurueckgenommenAm) continue;
        if (!keys.has(e.bestandteilKey)) continue;
        const bisher = aktiv.get(e.bestandteilKey);
        if (!bisher || zeitWert(e.erfasstAm) > zeitWert(bisher.erfasstAm)) aktiv.set(e.bestandteilKey, e);
    }
    const zeilen: BestandteilZeile[] = definition.map((bestandteil) => {
        const eintrag = aktiv.get(bestandteil.key) ?? null;
        const status: BestandteilZeile['status'] = !eintrag ? 'offen' : eintrag.art === 'entfaellt' ? 'entfaellt' : 'erledigt';
        return { bestandteil, status, eintrag };
    });
    const erledigt = zeilen.filter((z) => z.status === 'erledigt').length;
    const entfallen = zeilen.filter((z) => z.status === 'entfaellt').length;
    const gesamt = zeilen.length;
    const offen = gesamt - erledigt - entfallen;
    return { zeilen, gesamt, erledigt, entfallen, offen, vollstaendig: gesamt > 0 && offen === 0 };
};

/**
 * „4/5" — entfallene zaehlen als erledigt mit (sie halten die Position nicht
 * mehr auf); leer bei keinen Bestandteilen.
 */
export const standKurz = (stand: BestandteilStand): string =>
    !stand || stand.gesamt <= 0 ? '' : `${stand.erledigt + stand.entfallen}/${stand.gesamt}`;

// ─────────────────────────────────────────────────────────────────────────────
// Spiegel der Trigger-Regel (Uebernahme-Vorschlag)
// ─────────────────────────────────────────────────────────────────────────────

/** Das Wenige einer Zeile aus `projekt_unterlagen_ausgaben`, camelCase. */
export interface AusgabeLike {
    id: string;
    unterlage: string;
    anlass: string;
    erstelltAm: string;
    erstelltVonName?: string | null;
    fassungRef?: string | null;
}

/**
 * Erledigt diese Ausgabe diesen Bestandteil? Spiegel des Triggers (SQL Nr. 166).
 *
 * Gleiche Quelle (zeichengleich mit `unterlage`), und ein VERSAND erfuellt
 * auch `ab = 'freigabe'` (was versendet ist, war freigegeben), eine FREIGABE
 * erfuellt `ab = 'versand'` nicht. Unbekannter Anlass: nein.
 */
export const passtZuAusgabe = (
    b: PositionsBestandteil,
    ausgabe: Pick<AusgabeLike, 'unterlage' | 'anlass'>,
): boolean => {
    const ab = effektivesAb(b);
    if (!ab || !ausgabe || ausgabe.unterlage !== b.quelle) return false;
    if (ausgabe.anlass === 'versand') return true;
    if (ausgabe.anlass === 'freigabe') return ab === 'freigabe';
    return false;
};

/**
 * Genau EIN offener passender Bestandteil im Projekt → Treffer, sonst null.
 *
 * Kernregel 9 (Lehre applyInspectionCounter): stehen zwei Pakete mit
 * derselben Quelle offen, ist nicht zu entscheiden, welches die Ausgabe
 * meinte — dann erledigt die Automatik keinen, und die Liste fragt nach.
 * Derselbe (serviceId, key) zweimal in der Eingabe zaehlt einmal.
 */
export const passenderBestandteil = (
    positionen: ReadonlyArray<{ service: Pick<ServiceItem, 'id' | 'bestandteile'>; stand: BestandteilStand }>,
    ausgabe: Pick<AusgabeLike, 'unterlage' | 'anlass'>,
): { serviceId: string; key: string } | null => {
    const treffer = new Map<string, { serviceId: string; key: string }>();
    for (const p of positionen || []) {
        const serviceId = String(p?.service?.id ?? '');
        if (!serviceId) continue;
        for (const z of p?.stand?.zeilen || []) {
            if (z.status !== 'offen' || !passtZuAusgabe(z.bestandteil, ausgabe)) continue;
            treffer.set(`${serviceId}\u0000${z.bestandteil.key}`, { serviceId, key: z.bestandteil.key });
        }
    }
    return treffer.size === 1 ? [...treffer.values()][0] : null;
};

/**
 * Fuer eine offene Zeile: die juengste passende Ausgabe des Projekts
 * (Uebernahme-Vorschlag „Vorankündigung am 12.10. versendet — übernehmen?"),
 * sonst null. Gleich alte: die erste der Liste.
 */
export const uebernahmeVorschlag = (b: PositionsBestandteil, ausgaben: readonly AusgabeLike[]): AusgabeLike | null => {
    let beste: AusgabeLike | null = null;
    for (const a of ausgaben || []) {
        if (!a || !passtZuAusgabe(b, a)) continue;
        if (!beste || zeitWert(a.erstelltAm) > zeitWert(beste.erstelltAm)) beste = a;
    }
    return beste;
};

// ─────────────────────────────────────────────────────────────────────────────
// Anzeige: Datum, Uhrzeit, Person
// ─────────────────────────────────────────────────────────────────────────────

// Intl-Formatierer sind teuer im Aufbau — einmal je Modul, erst bei Bedarf.
let zeitFormat: Intl.DateTimeFormat | null = null;
let tagFormat: Intl.DateTimeFormat | null = null;

const teile = (fmt: Intl.DateTimeFormat, d: Date): Record<string, string> => {
    const out: Record<string, string> = {};
    for (const p of fmt.formatToParts(d)) out[p.type] = p.value;
    return out;
};

/**
 * '16.10.2026, 14:32 Uhr' in Europe/Berlin (Sommer/Winter korrekt).
 *
 * Immer in Europe/Berlin, nie in der Zeitzone des Rechners: ein Geraet in
 * einer anderen Zeitzone zeigte sonst eine andere Uhrzeit als der Nachweis. Leere Eingabe → ''; nicht lesbar → die Eingabe unveraendert (im
 * Nachweis lieber der rohe Wert als gar keiner).
 */
export const formatZeitpunkt = (iso: string): string => {
    const roh = String(iso ?? '').trim();
    if (!roh) return '';
    const ms = zeitWert(roh);
    if (!Number.isFinite(ms)) return roh;
    zeitFormat ??= new Intl.DateTimeFormat('de-DE', {
        timeZone: 'Europe/Berlin',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    });
    const t = teile(zeitFormat, new Date(ms));
    return `${t.day}.${t.month}.${t.year}, ${t.hour}:${t.minute} Uhr`;
};

/**
 * '15.10.2026' aus 'YYYY-MM-DD' (ohne Zeitzonenverschiebung — ein reiner
 * Kalendertag wird nie ueber Date geschickt, sonst wuerde '2026-10-15' westlich
 * von UTC zum 14.10.). Nicht lesbar → Eingabe unveraendert.
 */
export const formatTag = (tag: string): string => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(tag ?? '').trim());
    return m ? `${m[3]}.${m[2]}.${m[1]}` : String(tag ?? '');
};

const berlinTag = (d: Date): string => {
    tagFormat ??= new Intl.DateTimeFormat('de-DE', {
        timeZone: 'Europe/Berlin',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
    const t = teile(tagFormat, d);
    return `${t.year}-${t.month}-${t.day}`;
};

/**
 * Heutiger Kalendertag Europe/Berlin als 'YYYY-MM-DD' — Vorgabe und
 * Obergrenze des Leistungstags im Dialog. Nicht `toISOString().slice(0, 10)`:
 * das ist der UTC-Tag und zwischen Mitternacht und 1/2 Uhr Berliner Zeit noch
 * gestern. Ein ungueltiges `jetzt` faellt auf die aktuelle Zeit zurueck.
 */
export const heuteBerlin = (jetzt?: Date): string => {
    const d = jetzt instanceof Date && Number.isFinite(jetzt.getTime()) ? jetzt : new Date();
    return berlinTag(d);
};

export interface VerlaufEintrag {
    /** Roher Zeitstempel (Anzeige ueber formatZeitpunkt). */
    zeitpunkt: string;
    person: string;
    text: string;
    art:
        | 'erledigt'
        | 'entfaellt'
        | 'zurueckgenommen'
        | 'definition'
        | 'position_gebucht'
        | 'position_zurueckgenommen'
        /** Stunden eines Eintrags nachgetragen oder geaendert (Stundenprotokoll, art 'eintrag'). */
        | 'stunden'
        /** Stundenkontingent der Position gesetzt, geaendert oder entfernt (Stundenprotokoll, art 'kontingent'). */
        | 'kontingent';
}

// ─────────────────────────────────────────────────────────────────────────────
// Einmal-Schluessel der automatischen Positionsbuchung
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Praefix der automatischen Positionsbuchung im Protokoll. `BST-<serviceId>`
 * ist der Einmal-Riegel (Client und DB-Wache, Muster `FLAT-YYYY-MM`): je
 * Position hoechstens EINE aktive Buchung aus den Bestandteilen.
 */
export const BESTANDTEIL_BUCHUNG_PREFIX = 'BST-' as const;

export const bestandteilBuchungsRef = (serviceId: string): string => `${BESTANDTEIL_BUCHUNG_PREFIX}${serviceId}`;

export const istBestandteilBuchung = (e: { bookingReference?: string | null }): boolean =>
    typeof e?.bookingReference === 'string' && e.bookingReference.startsWith(BESTANDTEIL_BUCHUNG_PREFIX);

/**
 * Erbringer (`performerName`) der automatischen Positionsbuchung — den Inhalt
 * haben mehrere Personen erbracht, sie stehen in den Bestandteilen. Wer die
 * Buchung ausgeloest hat, steht in `bookedBy` (im Nachtlauf ebenfalls dieser
 * Name).
 */
export const BESTANDTEIL_SYSTEM_NAME = 'System (Bestandteile vollständig)' as const;

/**
 * Lokale Kopie von serviceReversalLogic.REVERSAL_REFERENCE_PREFIX (Blattmodul,
 * siehe Dateikopf). Ein Test haelt beide zeichengleich.
 */
const RUECKNAHME_PREFIX = 'RN-';

/**
 * Ist diese Protokollzeile die RN-Spur einer zurueckgenommenen
 * BESTANDTEIL-Positionsbuchung (`BST-…`)?
 *
 * Das Problem: planReversal ENTFERNT den zurueckgenommenen Eintrag aus dem
 * Protokoll (und das Journal liefert stillgelegte Zeilen nicht mehr aus,
 * journalToServiceTransactions). Die Spur selbst traegt eine Zufallsreferenz
 * `RN-JJJJ-NNNNN` und als Bezug nur `reversedEntryId` (id des entfernten
 * Eintrags) und `reversedReportRef` (dessen `reportRef`, falls er einen
 * hatte). Ob die entfernte Zeile eine `BST-`-Buchung war, steht also nur dann
 * fest, wenn einer dieser Bezuege es verraet. Erkannt wird darum an:
 *
 *  1. `reversedReportRef` beginnt mit 'BST-' — greift, wenn die
 *     Positionsbuchung ZUSAETZLICH `reportRef = 'BST-<serviceId>'` traegt
 *     (planReversal uebertraegt ihn dann von selbst). Belastbarster Weg.
 *  2. `reversedEntryId` ist die id einer `BST-`-Zeile in `bstIds` — greift,
 *     wenn der Aufrufer ein Protokoll MIT der entfernten Zeile hat (etwa
 *     Journalzeilen samt stillgelegten).
 *  3. `reversedEntryId` beginnt selbst mit 'BST-' — falls die
 *     Positionsbuchung eine abgeleitete id dieser Form bekommt.
 *
 * ANNAHME: Paket C (bestandteilBuchung) sorgt fuer 1. oder 3. Ohne einen der
 * drei Bezuege ist eine RN-Spur von der Ruecknahme einer gewoehnlichen Buchung
 * auf derselben Position nicht zu unterscheiden — sie wird dann bewusst NICHT
 * als Positionsruecknahme gezeigt (lieber eine fehlende Zeile im Verlauf als
 * eine falsch zugeordnete).
 */
export const istBestandteilRuecknahme = (
    t: Pick<ServiceTransaction, 'bookingReference' | 'reversedEntryId' | 'reversedReportRef'> | null | undefined,
    bstIds: ReadonlySet<string> = new Set(),
): boolean => {
    if (typeof t?.bookingReference !== 'string' || !t.bookingReference.startsWith(RUECKNAHME_PREFIX)) return false;
    const ref = String(t.reversedReportRef ?? '').trim();
    if (ref.startsWith(BESTANDTEIL_BUCHUNG_PREFIX)) return true;
    const id = String(t.reversedEntryId ?? '').trim();
    if (!id) return false;
    return id.startsWith(BESTANDTEIL_BUCHUNG_PREFIX) || bstIds.has(id);
};

// ─────────────────────────────────────────────────────────────────────────────
// Verlauf
// ─────────────────────────────────────────────────────────────────────────────

const UNBEKANNT = 'Unbekannt';

/**
 * Reihenfolge bei GLEICHEM Zeitpunkt (juengste zuerst = hoeherer Rang oben).
 * Eine Positionsbuchung folgt im selben Vorgang auf den letzten erledigten
 * Bestandteil bzw. auf die Aenderung der Liste, ihre Ruecknahme auf die
 * Ruecknahme des Bestandteils — bei sekundengleichen Server-Zeitstempeln soll
 * die Folge trotzdem stimmen.
 *
 * Stunden nachtragen folgt dem Erledigen (Pflichtfenster direkt danach) und
 * geht einer Ruecknahme voraus (ein zurueckgenommener Eintrag nimmt keine
 * Stunden mehr an) — deshalb zwischen beiden. Das Kontingent legt der Admin
 * nach der Liste fest — knapp ueber der Definition.
 */
const RANG: Record<VerlaufEintrag['art'], number> = {
    definition: 0,
    kontingent: 0.5,
    erledigt: 1,
    entfaellt: 1,
    stunden: 1.5,
    zurueckgenommen: 2,
    position_gebucht: 3,
    position_zurueckgenommen: 4,
};

const zitat = (s: string): string => `„${s}“`;

const stundenOderKeine = (h: number | null): string => (h === null ? 'keine' : formatStunden(h));

/** „Stunden zu „SiGe-Plan“: 8 h → 10 h" bzw. „… nachgetragen: 10 h". */
const stundenSatz = (wem: string, vorher: number | null, nachher: number | null): string =>
    vorher === null && nachher !== null
        ? `Stunden zu ${wem} nachgetragen: ${formatStunden(nachher)}`
        : `Stunden zu ${wem}: ${stundenOderKeine(vorher)} → ${stundenOderKeine(nachher)}`;

/** „Stundenkontingent: 80 h → 100 h", „… festgelegt: 100 h", „… entfernt (war 100 h)". */
const kontingentSatz = (vorher: number | null, nachher: number | null): string => {
    if (vorher === null && nachher !== null) return `Stundenkontingent festgelegt: ${formatStunden(nachher)}`;
    if (vorher !== null && nachher === null) return `Stundenkontingent entfernt (war ${formatStunden(vorher)})`;
    return `Stundenkontingent: ${stundenOderKeine(vorher)} → ${stundenOderKeine(nachher)}`;
};

/**
 * Alle Handlungen einer Position, juengste zuerst: erledigt, entfaellt,
 * zurueckgenommen (aus den Eintraegen), Aenderungen der Liste, und die
 * Positionsbuchungen aus dem Protokoll — Eintraege mit `BST-`-Referenz
 * (`bookedAt`/`bookedBy`) und deren RN-Spur (istBestandteilRuecknahme).
 *
 * Auch Eintraege zu inzwischen ENTFERNTEN Bestandteilen stehen drin: sie sind
 * geschehen, und der Verlauf ist der Nachweis. Ihr Label kommt aus der
 * Aenderungshistorie, sonst steht der Key da.
 *
 * Mit `stundenProtokoll` (Nr. 171) dazu jede Stunden- und Kontingentaenderung
 * (S7): „Stunden zu „SiGe-Plan“: 8 h → 10 h — Grund", „Stundenkontingent:
 * 80 h → 100 h". Den Bestandteil findet die Zeile ueber ihren Eintrag; fehlt
 * der in `eintraege`, heisst es „einem Bestandteil". Die Ersterfassung beim
 * Erledigen steht im Eintrag selbst und erscheint hier nicht eigens.
 */
export const verlauf = (args: {
    service: Pick<ServiceItem, 'id' | 'bestandteile'>;
    eintraege: readonly BestandteilEintrag[];
    aenderungen: readonly BestandteilAenderung[];
    serviceLog: readonly ServiceTransaction[];
    stundenProtokoll?: readonly StundenProtokollEintrag[];
}): VerlaufEintrag[] => {
    const serviceId = String(args?.service?.id ?? '');
    const definition = normalisiereBestandteile(args?.service?.bestandteile) ?? [];
    const aktuelleKeys = new Set(definition.map((b) => b.key));
    const aenderungen = (args?.aenderungen || []).filter((a) => a && String(a.serviceId) === serviceId);

    // Label je Key: zuerst die Historie (aelteste zuerst, juengere ueberschreiben),
    // zuletzt die aktuelle Definition — sie gewinnt immer.
    const labelJeKey = new Map<string, string>();
    [...aenderungen]
        .sort((a, b) => zeitVergleich(zeitWert(a.geaendertAm), zeitWert(b.geaendertAm)))
        .forEach((a) => [...(a.vorher || []), ...(a.nachher || [])].forEach((b) => labelJeKey.set(b.key, b.label)));
    definition.forEach((b) => labelJeKey.set(b.key, b.label));
    const name = (key: string): string =>
        zitat(labelJeKey.get(key) || key) + (aktuelleKeys.has(key) ? '' : ' (nicht mehr in der Liste)');

    const out: VerlaufEintrag[] = [];

    for (const e of args?.eintraege || []) {
        if (!e || String(e.serviceId) !== serviceId) continue;
        const wer = e.erfasstVonName || UNBEKANNT;
        if (e.art === 'entfaellt') {
            out.push({
                zeitpunkt: e.erfasstAm,
                person: wer,
                art: 'entfaellt',
                text: `${name(e.bestandteilKey)} entfällt${e.notiz ? ` — ${e.notiz}` : ''}`,
            });
        } else {
            // Leistungstag nur nennen, wenn er vom Erfassungstag abweicht —
            // „Leistungstag 15.10., erfasst 16.10. 14:32" (Plan, Nachweis).
            const erfasstMs = zeitWert(e.erfasstAm);
            const abweichend = Number.isFinite(erfasstMs) && e.leistungstag && berlinTag(new Date(erfasstMs)) !== e.leistungstag;
            out.push({
                zeitpunkt: e.erfasstAm,
                person: wer,
                art: 'erledigt',
                text:
                    `${name(e.bestandteilKey)} erledigt (${e.durch === 'modul' ? 'Modul' : 'von Hand'})` +
                    (abweichend ? `, Leistungstag ${formatTag(e.leistungstag)}` : '') +
                    (e.notiz ? ` — ${e.notiz}` : ''),
            });
        }
        if (e.zurueckgenommenAm) {
            out.push({
                zeitpunkt: e.zurueckgenommenAm,
                person: e.zurueckgenommenVonName || UNBEKANNT,
                art: 'zurueckgenommen',
                text:
                    `${name(e.bestandteilKey)} zurückgenommen (war ${e.art === 'entfaellt' ? 'entfallen' : 'erledigt'})` +
                    (e.zurueckgenommenGrund ? ` — ${e.zurueckgenommenGrund}` : ''),
            });
        }
    }

    for (const a of aenderungen) {
        const vorher = a.vorher || [];
        const nachher = a.nachher || [];
        let satz: string;
        if (vorher.length === 0 && nachher.length > 0) {
            satz = `Bestandteile festgelegt: ${nachher.map((b) => b.label).join(', ')}`;
        } else if (vorher.length > 0 && nachher.length === 0) {
            satz = 'Bestandteile entfernt — die Position ist kein Paket mehr';
        } else {
            const diff = definitionsDiff(vorher, nachher);
            const labelIn = (liste: PositionsBestandteil[], keys: string[]) =>
                keys.map((k) => liste.find((b) => b.key === k)?.label || k).join(', ');
            const teileSatz = [
                diff.neu.length ? `hinzugefügt: ${labelIn(nachher, diff.neu)}` : '',
                diff.entfernt.length ? `entfernt: ${labelIn(vorher, diff.entfernt)}` : '',
                diff.geaendert.length ? `geändert: ${labelIn(nachher, diff.geaendert)}` : '',
            ].filter(Boolean);
            satz = teileSatz.length
                ? `Bestandteile geändert — ${teileSatz.join('; ')}`
                : vorher.map((b) => b.key).join('\u0000') !== nachher.map((b) => b.key).join('\u0000')
                    ? 'Bestandteile geändert — Reihenfolge'
                    : 'Bestandteile neu gespeichert (ohne inhaltliche Änderung)';
        }
        out.push({ zeitpunkt: a.geaendertAm, person: a.geaendertVonName || UNBEKANNT, art: 'definition', text: satz });
    }

    // Key je Eintrag fuer die Stundenzeilen — ueber alle Eintraege, die ids
    // sind eindeutig; der Abgleich auf DIESE Position laeuft ueber die
    // Protokollzeile selbst (ihre service_id zieht per Fremdschluessel mit).
    const keyJeEintrag = new Map<string, string>();
    for (const e of args?.eintraege || []) if (e?.id) keyJeEintrag.set(String(e.id), e.bestandteilKey);
    for (const p of args?.stundenProtokoll || []) {
        if (!p || String(p.serviceId) !== serviceId) continue;
        const wer = p.geaendertVonName || UNBEKANNT;
        const grund = p.grund ? ` — ${p.grund}` : '';
        if (p.art === 'kontingent') {
            out.push({ zeitpunkt: p.geaendertAm, person: wer, art: 'kontingent', text: kontingentSatz(p.vorher, p.nachher) + grund });
        } else if (p.art === 'eintrag') {
            const key = p.eintragId ? keyJeEintrag.get(String(p.eintragId)) : undefined;
            out.push({
                zeitpunkt: p.geaendertAm,
                person: wer,
                art: 'stunden',
                text: stundenSatz(key ? name(key) : 'einem Bestandteil', p.vorher, p.nachher) + grund,
            });
        }
    }

    const eigeneZeilen = (args?.serviceLog || []).filter((t) => t && String(t.serviceId) === serviceId);
    const bstIds = new Set<string>();
    for (const t of eigeneZeilen) {
        if (!istBestandteilBuchung(t)) continue;
        if (t.id) bstIds.add(String(t.id));
        // Den Text formuliert der Verlauf selbst: die `description` der
        // BST-Zeile ist bewusst NEUTRAL (der Positionstitel), weil sie im
        // Leistungsnachweis steht (Kernregel 6 — keine Bestandteile auf
        // Kundendokumenten). Wer ausgeloest hat, sagt `bookedBy`.
        out.push({
            zeitpunkt: t.bookedAt || t.date,
            person: t.bookedBy || t.performerName || UNBEKANNT,
            art: 'position_gebucht',
            text: 'Position automatisch gebucht — alle Bestandteile erledigt',
        });
    }
    for (const t of eigeneZeilen) {
        if (!istBestandteilRuecknahme(t, bstIds)) continue;
        // Die Spur beginnt ihren Text mit „Buchung zurückgenommen: …"
        // (planReversal) — doppelt muss das nicht dastehen.
        const rest = String(t.description || '').replace(/^Buchung zurückgenommen:\s*/, '').trim();
        out.push({
            zeitpunkt: t.bookedAt || t.date,
            person: t.bookedBy || t.performerName || UNBEKANNT,
            art: 'position_zurueckgenommen',
            text: `Positionsbuchung zurückgenommen${rest ? ` — ${rest}` : ''}`,
        });
    }

    // Stabil sortieren: juengste zuerst, bei gleichem Zeitpunkt nach RANG,
    // sonst in Einfuegereihenfolge. Unlesbare Zeitpunkte ans Ende.
    return out
        .map((v, i) => ({ v, i, ms: zeitWert(v.zeitpunkt) }))
        .sort((x, y) => {
            if (x.ms !== y.ms) return zeitVergleich(y.ms, x.ms);
            if (RANG[x.v.art] !== RANG[y.v.art]) return RANG[y.v.art] - RANG[x.v.art];
            return x.i - y.i;
        })
        .map((x) => x.v);
};

// ─────────────────────────────────────────────────────────────────────────────
// Bruecke zur LV-Zuordnung (Projektcheck, Entscheid E9)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Welche LV-Zuordnung (`ServiceAssignment`) eine EIGENE Position fuer dieselbe
 * Unterlage traegt. Ein `Record` ueber die vollstaendige `BestandteilQuelle`:
 * eine neue Quelle bricht den Typecheck, statt still auf „keine Zuordnung" zu
 * fallen.
 *
 *  - `handbuch` → null: fuer das Koordinierungshandbuch gibt es keinen
 *    Zuordnungswert (LV_JE_UNTERLAGE kennt keine Handbuchposition, Plan B5) —
 *    eine Doppelung waere nur ueber den Titel zu raten, und geraten wird nicht.
 *  - `hand` → null: kein Modul, keine Unterlage, nichts zu verdoppeln.
 */
const ZUORDNUNG_JE_QUELLE: Record<BestandteilQuelle, import('../../types').ServiceAssignment | null> = {
    vorankuendigung: 'vorank',
    sigeplan: 'sigeplan',
    asplan: 'asplan',
    unterlage: 'unterlage',
    baustellenordnung: 'site_rules',
    handbuch: null,
    hand: null,
};

/**
 * Die LV-Zuordnung, unter der eine Unterlage als eigene Position im Projekt
 * stuende — Grundlage der Projektcheck-Regel `bestandteil_doppelt_als_position`
 * (E9: erlaubt, aber gelb). Geprueft wird ueber die ZUORDNUNG, nie ueber ein
 * Titelwort (Lehre billingSelection: der Textarm boete sonst fremde
 * Positionen an). Unbekannte Quelle → null.
 */
export const zuordnungFuerQuelle = (q: BestandteilQuelle): import('../../types').ServiceAssignment | null =>
    (Object.prototype.hasOwnProperty.call(ZUORDNUNG_JE_QUELLE, q) ? ZUORDNUNG_JE_QUELLE[q] : null) ?? null;
